// Question Card Component
// Individual card in the deck with question and answers

import React, { useMemo } from 'react';
import {
  QuestionCardWrapper,
  QuestionCard as StyledQuestionCard,
  CardContent,
  QuestionText,
  AnswerOptions,
  AnswerButton,
  SwipeHint,
  CardSuitCorner,
  CardSuitSymbol,
} from '../../../styles/HeartGames/QuestionCard.styles';
import { QuestionCard as QuestionCardType } from '../../../services/HeartGames/questionService';

// Playing card suits (matching swipe deck)
const SUITS = [
  { symbol: '♠', color: '#000000', name: 'spades' },
  { symbol: '♥', color: '#DC143C', name: 'hearts' },
  { symbol: '♦', color: '#DC143C', name: 'diamonds' },
  { symbol: '♣', color: '#000000', name: 'clubs' }
];

interface QuestionCardProps {
  question: QuestionCardType;
  questionNumber: number;
  totalQuestions: number;
  selectedAnswerId: string | null;
  onAnswerSelect: (answerId: string) => void;
  zIndex: number;
  isTopCard: boolean;
  offset: number;
  rotation: number;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswerId,
  onAnswerSelect,
  zIndex,
  isTopCard,
  offset,
  rotation,
}) => {
  // Generate consistent suit based on question ID
  const cardSuit = useMemo(() => {
    const hash = question.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return SUITS[hash % SUITS.length];
  }, [question.id]);

  return (
    <QuestionCardWrapper
      $zIndex={zIndex}
      $isTopCard={isTopCard}
      $offset={offset}
      $rotation={rotation}
    >
      <StyledQuestionCard $isSelected={!!selectedAnswerId}>
        {/* Card Suit Corner Badges */}
        <CardSuitCorner $suit={cardSuit.name} $color={cardSuit.color} $position="top-left">
          <CardSuitSymbol $color={cardSuit.color}>{cardSuit.symbol}</CardSuitSymbol>
        </CardSuitCorner>
        <CardSuitCorner $suit={cardSuit.name} $color={cardSuit.color} $position="bottom-right">
          <CardSuitSymbol $color={cardSuit.color}>{cardSuit.symbol}</CardSuitSymbol>
        </CardSuitCorner>

        <CardContent>
          <QuestionText>{question.question}</QuestionText>

          <AnswerOptions>
            {question.options.map((option) => (
              <AnswerButton
                key={option.id}
                $isSelected={selectedAnswerId === option.id}
                onClick={() => isTopCard && onAnswerSelect(option.id)}
                disabled={!isTopCard}
              >
                {option.text}
              </AnswerButton>
            ))}
          </AnswerOptions>
        </CardContent>

        {isTopCard && selectedAnswerId && (
          <SwipeHint $show={true}>
            Swipe to continue →
          </SwipeHint>
        )}
      </StyledQuestionCard>
    </QuestionCardWrapper>
  );
};
